import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Typography,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TranslateIcon from "@mui/icons-material/Translate";
import ScienceIcon from "@mui/icons-material/Science";
import {
  fetchOrderLanguageById,
  updateOrderLanguage,
  fetchOrderSubstrates,
  updateLabTestSubstrate,
} from "../../../Services/apiService";

// Visual language borrowed from SettingsDialog.js / AccountSettings.js, so
// this popup reads as the same "app settings" surface rather than a
// one-off dialog: grey nav column + light content panel inside a fixed,
// rounded, shadowed shell, plain #EBEBEB fields, and a top-right-only close
// button (no DialogTitle bar).
const NAV_ITEMS = [
  { key: "language", label: "Edit Language", Icon: TranslateIcon },
  { key: "substrate", label: "Edit Substrate", Icon: ScienceIcon },
];

const selectSx = {
  minWidth: 190,
  borderRadius: 1.5,
  fontSize: "0.8125rem",
  bgcolor: "#EBEBEB",
  "& fieldset": { borderColor: "#EBEBEB" },
  "&:hover fieldset": { borderColor: "#d8d8d8" },
  "&.Mui-focused fieldset": { borderColor: "#cfcfcf" },
  "& .MuiSelect-select": { py: 0.875 },
};

const actionButtonSx = {
  textTransform: "none",
  borderRadius: 1.5,
  px: 2.5,
  py: 0.5,
  minHeight: 0,
  fontSize: "0.8125rem",
  color: "#1a1a1a",
  bgcolor: "#EBEBEB",
  boxShadow: "none",
  "&:hover": { bgcolor: "#E0E0E0", boxShadow: "none" },
};

const secondaryButtonSx = {
  textTransform: "none",
  borderRadius: 1.5,
  px: 2.5,
  py: 0.5,
  minHeight: 0,
  fontSize: "0.8125rem",
  color: "#4b5563",
  bgcolor: "#EBEBEB",
  boxShadow: "none",
  "&:hover": { bgcolor: "#E0E0E0", boxShadow: "none" },
};

function NavButton({ item, active, onClick }) {
  const { Icon } = item;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.25,
        py: 0.875,
        borderRadius: 1.5,
        cursor: "pointer",
        bgcolor: active ? "#FFFFFF" : "transparent",
        color: active ? "#1a1a1a" : "#6b7280",
        boxShadow: active ? "0 1px 2px rgba(16,24,40,0.06)" : "none",
        transition: "background-color 0.12s, color 0.12s",
        "&:hover": { bgcolor: active ? "#FFFFFF" : "#E4E4E4" },
      }}
    >
      <Icon sx={{ fontSize: 18, color: active ? "#1a1a1a" : "#9ca3af" }} />
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: active ? 500 : 400 }}>{item.label}</Typography>
    </Box>
  );
}

function Row({ label, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 3,
        py: 2,
        borderBottom: "1px solid #e5e7eb",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography sx={{ fontSize: "0.8125rem", color: "#111827" }}>{label}</Typography>
      <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>{children}</Box>
    </Box>
  );
}

// Order edit popup, opened from the order detail header's Edit icon.
// Left nav switches between two independent panels sharing one dialog:
//  - Edit Language: GET/POST manageEditOrders/orderLanguage(+update) — looked
//    up by the RAW INTERNAL order id, but order_number (from that response)
//    is what the update call and the substrate lookup below both key on.
//  - Edit Substrate: GET/POST manageEditOrders/orderSubstrates(+update),
//    one row per test on the order, keyed by order_number + test_identifier.
function EditOrderDialog({ open, internalOrderId, onClose, notify, onSubstrateUpdated }) {
  const [tab, setTab] = React.useState("language");

  const [languageInfo, setLanguageInfo] = React.useState(null);
  const [languageLoading, setLanguageLoading] = React.useState(false);
  const [languageError, setLanguageError] = React.useState("");
  const [languageUpdating, setLanguageUpdating] = React.useState(false);
  const [pendingLanguage, setPendingLanguage] = React.useState(null);

  const [substrateTests, setSubstrateTests] = React.useState([]);
  const [availableSubstrates, setAvailableSubstrates] = React.useState([]);
  const [substrateLoading, setSubstrateLoading] = React.useState(false);
  const [substrateError, setSubstrateError] = React.useState("");
  const [updatingTestId, setUpdatingTestId] = React.useState(null);
  const [pendingSubstrate, setPendingSubstrate] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTab("language");
    setPendingLanguage(null);
    setPendingSubstrate(null);
    setLanguageLoading(true);
    setLanguageError("");
    fetchOrderLanguageById(internalOrderId)
      .then((res) => {
        if (!cancelled) setLanguageInfo(res);
      })
      .catch((err) => {
        if (!cancelled) setLanguageError(err.message || "Failed to load order language.");
      })
      .finally(() => {
        if (!cancelled) setLanguageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, internalOrderId]);

  // Runs once order_number is known from the language lookup above -
  // orderSubstrates is keyed by order_number, not the internal id.
  React.useEffect(() => {
    if (!open || !languageInfo?.order_number) return;
    let cancelled = false;
    setSubstrateLoading(true);
    setSubstrateError("");
    fetchOrderSubstrates(languageInfo.order_number)
      .then((res) => {
        if (cancelled) return;
        setSubstrateTests(res?.data || []);
        setAvailableSubstrates(res?.availableSubstrates || []);
      })
      .catch((err) => {
        if (!cancelled) setSubstrateError(err.message || "Failed to load substrates.");
      })
      .finally(() => {
        if (!cancelled) setSubstrateLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, languageInfo?.order_number]);

  const handleLanguageSelect = (e) => {
    const language = e.target.value;
    if (!languageInfo || language === languageInfo.order_language) return;
    setPendingLanguage(language);
  };

  const confirmLanguageChange = async () => {
    if (!languageInfo || !pendingLanguage) return;
    setLanguageUpdating(true);
    try {
      await updateOrderLanguage(languageInfo.order_number, pendingLanguage);
      setLanguageInfo((prev) => (prev ? { ...prev, order_language: pendingLanguage } : prev));
      notify?.(`Order language updated to ${pendingLanguage}.`);
    } catch (err) {
      notify?.(err.message || "Failed to update order language.", "error");
    } finally {
      setLanguageUpdating(false);
      setPendingLanguage(null);
    }
  };

  // "Unknown" is a real stored value (phantom line-item tests, e.g. shipping
  // fees) but the update endpoint only accepts the 5 real substrate names -
  // it's excluded from the selectable option list below, so it can only ever
  // be the FROM side of a change, never the TO side.
  const handleSubstrateSelect = (testIdentifier, currentSubstrate, newSubstrate) => {
    if (newSubstrate === currentSubstrate) return;
    setPendingSubstrate({ testIdentifier, from: currentSubstrate, to: newSubstrate });
  };

  const confirmSubstrateChange = async () => {
    if (!pendingSubstrate || !languageInfo?.order_number) return;
    const { testIdentifier, to } = pendingSubstrate;
    setUpdatingTestId(testIdentifier);
    try {
      await updateLabTestSubstrate(languageInfo.order_number, testIdentifier, to);
      setSubstrateTests((prev) =>
        prev.map((t) => (t.test_identifier === testIdentifier ? { ...t, substrate: to } : t))
      );
      onSubstrateUpdated?.(testIdentifier, to);
      notify?.(`Substrate for ${testIdentifier} updated to ${to}.`);
    } catch (err) {
      notify?.(err.message || "Failed to update substrate.", "error");
    } finally {
      setUpdatingTestId(null);
      setPendingSubstrate(null);
    }
  };

  const busy = languageUpdating || Boolean(updatingTestId);

  return (
    <>
      <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "min(680px, 92vw)",
            height: "min(520px, 82vh)",
            maxWidth: "none",
            maxHeight: "none",
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid #e5e7eb",
            boxShadow: "0 24px 64px rgba(16,24,40,0.18), 0 4px 12px rgba(16,24,40,0.08)",
          },
        }}
      >
        <Box sx={{ display: "flex", height: "100%", minHeight: 0 }}>
          {/* Left nav */}
          <Box sx={{ width: 190, flexShrink: 0, bgcolor: "#EBEBEB", borderRight: "1px solid #e5e7eb", overflowY: "auto" }}>
            <Box sx={{ px: 1.25, py: 1.5 }}>
              <Typography
                sx={{
                  px: 1.25,
                  mb: 0.75,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#9ca3af",
                }}
              >
                Order
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                {NAV_ITEMS.map((item) => (
                  <NavButton key={item.key} item={item} active={tab === item.key} onClick={() => setTab(item.key)} />
                ))}
              </Box>
            </Box>
          </Box>

          {/* Right column: static header + scrollable content */}
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", bgcolor: "#F1F1F1" }}>
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", px: 1.25, py: 0.75 }}>
              <IconButton
                onClick={onClose}
                disabled={busy}
                aria-label="Close"
                size="small"
                sx={{ bgcolor: "#FFFFFF", border: "1px solid #e5e7eb", "&:hover": { bgcolor: "#F9FAFB" } }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
                {tab === "language" && (
                  <>
                    <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#111827", mb: 1.5 }}>
                      Edit Language
                    </Typography>
                    {languageLoading && (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={20} sx={{ color: "#9ca3af" }} />
                      </Box>
                    )}
                    {!languageLoading && languageError && (
                      <Alert severity="error" sx={{ fontSize: "0.8125rem" }}>
                        {languageError}
                      </Alert>
                    )}
                    {!languageLoading && !languageError && languageInfo && (
                      <Box>
                        <Row label="Country">
                          <Typography sx={{ fontSize: "0.8125rem", color: "#111827" }}>
                            {languageInfo.country || "—"}
                          </Typography>
                        </Row>
                        <Row label="Language">
                          <FormControl size="small" disabled={languageUpdating}>
                            <Select
                              value={languageInfo.order_language || ""}
                              onChange={handleLanguageSelect}
                              displayEmpty
                              renderValue={(value) => value || "Not set"}
                              sx={selectSx}
                            >
                              {(languageInfo.availableLanguages || []).map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Row>
                      </Box>
                    )}
                  </>
                )}

                {tab === "substrate" && (
                  <>
                    <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#111827", mb: 1.5 }}>
                      Edit Substrate
                    </Typography>
                    {substrateLoading && (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={20} sx={{ color: "#9ca3af" }} />
                      </Box>
                    )}
                    {!substrateLoading && substrateError && (
                      <Alert severity="error" sx={{ fontSize: "0.8125rem" }}>
                        {substrateError}
                      </Alert>
                    )}
                    {!substrateLoading && !substrateError && substrateTests.length === 0 && (
                      <Typography sx={{ fontSize: "0.8125rem", color: "#9ca3af" }}>No tests found on this order.</Typography>
                    )}
                    {!substrateLoading && !substrateError && substrateTests.length > 0 && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                        {substrateTests.map((test) => (
                          <Box
                            key={test.test_identifier}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                              p: 1.5,
                              bgcolor: "#FFFFFF",
                              border: "1px solid #e5e7eb",
                              borderRadius: 2,
                              boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#111827" }}>
                                {test.test_identifier}
                              </Typography>
                              {test.progress_status && (
                                <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af", mt: 0.25 }}>
                                  {test.progress_status}
                                </Typography>
                              )}
                            </Box>
                            <FormControl size="small" disabled={updatingTestId === test.test_identifier}>
                              <Select
                                value={test.substrate || ""}
                                onChange={(e) => handleSubstrateSelect(test.test_identifier, test.substrate, e.target.value)}
                                displayEmpty
                                renderValue={(value) => value || "Not set"}
                                sx={selectSx}
                              >
                                {test.substrate === "Unknown" && (
                                  <MenuItem value="Unknown" disabled>
                                    Unknown
                                  </MenuItem>
                                )}
                                {availableSubstrates
                                  .filter((option) => option !== "Unknown")
                                  .map((option) => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(pendingLanguage)}
        onClose={() => setPendingLanguage(null)}
        aria-labelledby="language-confirmation-dialog"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle id="language-confirmation-dialog" sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
          Confirm Language Update
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.8125rem" }}>
            Change the email language for Order {languageInfo?.order_number} from "{languageInfo?.order_language}" to "
            {pendingLanguage}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setPendingLanguage(null)} disabled={languageUpdating} sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button onClick={confirmLanguageChange} autoFocus disabled={languageUpdating} sx={actionButtonSx}>
            {languageUpdating ? <CircularProgress size={16} sx={{ color: "#1a1a1a" }} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingSubstrate)}
        onClose={() => setPendingSubstrate(null)}
        aria-labelledby="substrate-confirmation-dialog"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle id="substrate-confirmation-dialog" sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
          Confirm Substrate Update
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.8125rem" }}>
            Change the substrate for Test {pendingSubstrate?.testIdentifier} from "{pendingSubstrate?.from}" to "
            {pendingSubstrate?.to}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setPendingSubstrate(null)} disabled={Boolean(updatingTestId)} sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button onClick={confirmSubstrateChange} autoFocus disabled={Boolean(updatingTestId)} sx={actionButtonSx}>
            {updatingTestId ? <CircularProgress size={16} sx={{ color: "#1a1a1a" }} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EditOrderDialog;
